import type { DataPointModel } from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile/models/overview.model';
import type {
	AnalyticsInfoResponse,
	AnalyticsVolumeResponse
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-analytics.model';
import type { ReviewSentimentResponse } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-reviews.model';
import type { BestTimeToPostCell } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-best-time-to-post.service';
import type { AnalyticsEmotionResponse } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-emotion.service';
import type { EmotionDistribution } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-emotion-distribution.service';
import type { MediaTypeInfluencer } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-influencers-by-media-type.service';
import type { LanguageTop } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-languages.service';
import type { MentionsByMediaTypePlatform } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-mentions-by-media-type.service';
import type { AnalyticsSentimentByPlatformResponse } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-sentiment-by-platform.service';
import type { TopCountryItem } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-top-countries.service';
import type { TopInfluencerByVoiceShare } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-top-influencers-by-voice-share.service';
import type { TopSiteByVisits } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-top-sites-by-visits.service';
import type { TrendingConversation } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-trending-conversations.service';
import type { TrendingHashtag } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-trending-hashtags.service';
import {
	assignEntityColors,
	blockEntityNames,
	ENTITY_COLUMN_KEY,
	ENTITY_FIGURE_SEPARATOR
} from './report-block-chart-config';
import { ReportBlockData, ReportBlockRender } from './report-blocks.service';
import {
	chartTypeAdviceFor,
	ReportSectionChartSwitch,
	sectionChartSwitch
} from './report-chart-types';

/**
 * The fixed sections of a report WITH A SOURCE — a competitor project or an
 * industry — drawn about that source rather than about the brand (T-2928).
 *
 * The backend answers each section with the catalogue blocks it is made of
 * (`POST /reports/{id}/sections/resolve`, see ReportSourceSectionsService),
 * resolved for the source's entities and already judged: a section too thin
 * for an entity, or one that cannot compare several, comes back empty with a
 * note saying why. What is decided here is only how the answer is drawn:
 *
 * - `own` — one entity (an industry, a project of one competitor): the
 *   section's own slide, fed the answer in the shape its analytics service
 *   returns (the functions at the end of this file), so it looks exactly as it
 *   does for a brand;
 * - `compare` — several entities: one slide, the merged block drawn in its
 *   chart area, one series per entity (SourceComparisonSlideComponent);
 * - `note` — nothing to draw: the section's frame with the answer's note.
 *
 * The blocks are read by the series keys and columns the backend's resolver
 * writes (`reports/blocks/resolver.py`), never by block id: which blocks a
 * section is made of is the backend's call (`findings.SECTION_BLOCKS`).
 */

export const REPORT_SINGLE_ENTITY_SECTION_IDS: ReadonlySet<string> = new Set([
	'sentiment_over_time',
	'emotion_over_time',
	'sentiment_by_media_type',
	'emotion_analysis',
	'influencers_by_media_type',
	'best_time_to_post'
]);

export const REPORT_COMPARED_SECTION_IDS: ReadonlySet<string> = new Set([
	'executive_summary',
	'volume_of_mentions_reach',
	'top_influencers_by_voice_share',
	'top_sites_by_visits',
	'top_countries',
	'trending_hashtags',
	'trending_conversations',
	'mentions_by_media_type',
	'languages'
]);

// TRAP: every other section is a request of its own — in one with the rest,
// a slow read would hold up every slide. A group shares its aggregation.
export const REPORT_SOURCE_SECTION_BATCHES: readonly (readonly string[])[] = [
	['volume_of_mentions_reach', 'sentiment_over_time', 'emotion_over_time'],
	[
		'sentiment_by_media_type',
		'influencers_by_media_type',
		'mentions_by_media_type'
	]
];

// CONTRACT: a fixed section in neither set is the brand's only — Website
// traffic demographics, which reads the brand's own site.
export function isSourceSection(sectionId: string): boolean {
	return (
		REPORT_SINGLE_ENTITY_SECTION_IDS.has(sectionId) ||
		REPORT_COMPARED_SECTION_IDS.has(sectionId)
	);
}

export function sourceSectionBatch(sectionId: string): string {
	const batch = REPORT_SOURCE_SECTION_BATCHES.find(group =>
		group.includes(sectionId)
	);
	return batch ? batch.join(',') : sectionId;
}

export type ReportSourceLayout = 'own' | 'compare' | 'note';

export function sourceSectionLayout(
	sectionId: string,
	blocks: ReportBlockData[] | null
): ReportSourceLayout {
	if (!isSourceSection(sectionId)) {
		return 'note';
	}
	if (blocks === null) {
		return 'own';
	}
	const drawn = blocks.filter(block => !block.empty);
	if (!drawn.length) {
		return 'note';
	}
	if (sourceSectionEntities(drawn).length > 1) {
		return 'compare';
	}
	// TRAP: a comparable section that dropped an entity names it in a note,
	// which only a comparison prints — so it is one even with one entity left.
	if (
		REPORT_COMPARED_SECTION_IDS.has(sectionId) &&
		drawn.some(block => block.note)
	) {
		return 'compare';
	}
	if (sectionId === 'executive_summary' && !sourceHeadlineTiles(drawn)) {
		return 'compare';
	}
	return 'own';
}

export function sourceSectionNote(blocks: ReportBlockData[]): string | null {
	return blocks.find(block => block.note)?.note ?? null;
}

export function sourceSectionEntities(blocks: ReportBlockData[]): string[] {
	const names = blocks.flatMap(block =>
		block.empty ? [] : blockEntityNames(block)
	);
	return Array.from(new Set(names));
}

// CONTRACT: the backend lists a section's blocks chart first
// (`findings.SECTION_BLOCKS`), so Volume of Mentions & Reach compares its
// mentions. The Executive Summary compares its headline figures, whichever
// block holds them.
export function sourceComparisonBlock(
	sectionId: string,
	blocks: ReportBlockData[]
): ReportBlockData | null {
	const drawn = blocks.filter(block => !block.empty);
	if (sectionId === 'executive_summary') {
		return (
			drawn.find(block => Object.keys(block.figures ?? {}).length) ??
			drawn[0] ??
			null
		);
	}
	return drawn[0] ? withinOneSlide(sectionId, drawn[0]) : null;
}

// CONTRACT: the ten each of these sections' own slides ranks.
const COMPARED_TOP_LABELS: Readonly<Record<string, number>> = {
	top_countries: 10,
	trending_hashtags: 10,
	trending_conversations: 10,
	languages: 10
};

// TRAP: twenty, shared out between the entities — two entities' own top
// tens, which were always drawn whole, still are.
const COMPARED_ROWS = 20;

// TRAP: each entity comes back with its own top N, so five of them are up to
// a hundred hashtags or fifty accounts — bars and ticks piled on each other.
// A breakdown keeps the ten its slide ranks, tabled by entity rather than as
// each entity's list; a list keeps each entity's own leaders.
function withinOneSlide(
	sectionId: string,
	block: ReportBlockData
): ReportBlockData {
	const entities = blockEntityNames(block);
	if (entities.length < 2) {
		return block;
	}
	if (block.shape === 'categories') {
		// CONTRACT: a merged breakdown's labels come ordered by all its
		// entities together (`comparison._ordered_labels`), so its first ten
		// are the ten the section ranks.
		const top = COMPARED_TOP_LABELS[sectionId] ?? block.labels.length;
		return {
			...block,
			labels: block.labels.slice(0, top),
			series: block.series.map(one => ({
				...one,
				values: one.values.slice(0, top)
			})),
			columns: [],
			rows: []
		};
	}
	if (block.shape === 'rows') {
		const each = Math.max(1, Math.floor(COMPARED_ROWS / entities.length));
		const kept = new Map<string, number>();
		return {
			...block,
			rows: block.rows.filter(row => {
				const entity = String(row[ENTITY_COLUMN_KEY] ?? '');
				const count = kept.get(entity) ?? 0;
				kept.set(entity, count + 1);
				return count < each;
			})
		};
	}
	return block;
}

export interface ReportEntityMatrix {
	measures: string[];
	rows: Array<{
		entity: string;
		color: string;
		values: string[];
	}>;
}

export function entityMatrix(
	block: ReportBlockData | null
): ReportEntityMatrix | null {
	const entities = block ? blockEntityNames(block) : [];
	if (!block || entities.length < 2) {
		return null;
	}
	const measures: string[] = [];
	const values = new Map<string, string>();
	for (const [key, value] of Object.entries(block.figures ?? {})) {
		const at = key.indexOf(ENTITY_FIGURE_SEPARATOR);
		if (at <= 0) {
			continue;
		}
		const measure = key.slice(at + ENTITY_FIGURE_SEPARATOR.length);
		if (!measures.includes(measure)) {
			measures.push(measure);
		}
		values.set(key, value);
	}
	if (!measures.length) {
		return null;
	}
	const colors = assignEntityColors(entities);
	return {
		measures,
		rows: entities.map(entity => ({
			entity,
			color: colors.get(entity) ?? '',
			values: measures.map(
				measure =>
					values.get(
						`${entity}${ENTITY_FIGURE_SEPARATOR}${measure}`
					) ?? '—'
			)
		}))
	};
}

export function comparisonChartSwitch(
	sectionId: string,
	chartType: ReportBlockRender | null | undefined,
	block: ReportBlockData | null
): ReportSectionChartSwitch {
	const own = sectionChartSwitch(sectionId, chartType, block);
	const takes = block?.renders?.length ? new Set(block.renders) : null;
	const types = takes ? own.types.filter(type => takes.has(type)) : own.types;
	// TRAP: stat cards are drawn from figures alone — a block of rows without
	// them, with no type to switch to, is tabled.
	const unswitched: ReportBlockRender | undefined =
		block && !Object.keys(block.figures ?? {}).length && block.rows?.length
			? 'table'
			: block?.render;
	const initial = types.includes(own.designedAs)
		? own.designedAs
		: types[0] ?? unswitched ?? own.designedAs;
	const current = types.includes(own.current) ? own.current : initial;
	return {
		...own,
		types,
		current,
		// TRAP: ALWAYS from the block, even at the section's own design — that
		// design is of one entity, and there is none under it to fall back to.
		block: block ? { ...block, render: current } : null,
		advice: chartTypeAdviceFor(block, types)
	};
}

const HEADLINE_TILE_KEYS = [
	'total_mentions',
	'likes',
	'positive_mentions',
	'social_media_reach',
	'web_mentions',
	'total_reach',
	'negative_mentions',
	'social_mentions',
	'total_engagements',
	'web_sites'
];

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const EMOTIONS: readonly (keyof EmotionDistribution)[] = [
	'love',
	'joy',
	'surprise',
	'anger',
	'sadness',
	'fear',
	'disgust'
];

function drawnBlock(blocks: ReportBlockData[]): ReportBlockData | null {
	return blocks.find(block => !block.empty) ?? null;
}

function numberOf(value: unknown): number {
	const number = Number(value);
	return value === null || value === '' || !Number.isFinite(number)
		? 0
		: number;
}

function numberOrNull(value: unknown): number | null {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
}

function textOf(value: unknown): string {
	return value === null || value === undefined ? '' : String(value).trim();
}

// CONTRACT: to the hundredth, as the analytics services round their shares.
function percentOf(part: number, whole: number): number {
	return whole > 0 ? Math.round((part / whole) * 10000) / 100 : 0;
}

// CONTRACT: the resolver names a platform with `display_platform_name`, whose
// every name lower-cases back to the stored id the slides map from.
function platformId(label: string): string {
	return label.toLowerCase();
}

function lineOf(blocks: ReportBlockData[], key: string): DataPointModel[] {
	for (const block of blocks) {
		const series = block.empty
			? undefined
			: block.series?.find(one => one.key === key);
		if (series) {
			return (block.labels ?? []).map((timestamp, index) => ({
				timestamp,
				value: series.values[index] ?? 0
			}));
		}
	}
	return [];
}

function countsOf(
	blocks: ReportBlockData[]
): Array<{ label: string; count: number }> {
	const block = drawnBlock(blocks);
	const values = block?.series?.[0]?.values ?? [];
	return (block?.labels ?? []).map((label, index) => ({
		label,
		count: values[index] ?? 0
	}));
}

// CONTRACT: the over-time series are keyed by the warehouse column they count
// (`_one_series`): mentions, reach, and interactions for engagement.
export function sourceVolume(
	blocks: ReportBlockData[]
): AnalyticsVolumeResponse {
	return {
		mentionsData: lineOf(blocks, 'mentions'),
		reachData: lineOf(blocks, 'reach'),
		socialMediaInteractionsData: lineOf(blocks, 'interactions')
	};
}

export function sourceSentimentOverTime(
	blocks: ReportBlockData[]
): ReviewSentimentResponse {
	return {
		positiveSentimentData: lineOf(blocks, 'positive'),
		neutralSentimentData: lineOf(blocks, 'neutral'),
		negativeSentimentData: lineOf(blocks, 'negative')
	};
}

// CONTRACT: the resolver leaves out an emotion nobody expressed, which the
// analytics endpoint sends as a line of zeros — drawn as one here.
export function sourceEmotionOverTime(
	blocks: ReportBlockData[]
): AnalyticsEmotionResponse {
	const block = drawnBlock(blocks);
	const days = block?.labels ?? [];
	const line = (key: string): DataPointModel[] => {
		const series = block?.series?.find(one => one.key === key);
		return days.map((timestamp, index) => ({
			timestamp,
			value: series?.values[index] ?? 0
		}));
	};
	return {
		loveData: line('love'),
		joyData: line('joy'),
		surpriseData: line('surprise'),
		angerData: line('anger'),
		sadnessData: line('sadness'),
		fearData: line('fear'),
		disgustData: line('disgust')
	};
}

// CONTRACT: percentages, as the analytics endpoint sends them — of the three
// counts together, since the block carries no platform total.
export function sourceSentimentByPlatform(
	blocks: ReportBlockData[]
): AnalyticsSentimentByPlatformResponse {
	const block = drawnBlock(blocks);
	const values = (key: string): Array<number | null> =>
		block?.series?.find(one => one.key === key)?.values ?? [];
	const positive = values('positive');
	const neutral = values('neutral');
	const negative = values('negative');
	return {
		platforms: (block?.labels ?? []).map((label, index) => {
			const counts = [
				positive[index] ?? 0,
				neutral[index] ?? 0,
				negative[index] ?? 0
			];
			const total = counts[0] + counts[1] + counts[2];
			return {
				platform: platformId(label),
				positive: percentOf(counts[0], total),
				neutral: percentOf(counts[1], total),
				negative: percentOf(counts[2], total)
			};
		})
	};
}

// CONTRACT: an emotion the resolver left out was not expressed.
export function sourceEmotionDistribution(
	blocks: ReportBlockData[]
): EmotionDistribution {
	const counts = new Map(
		countsOf(blocks).map(entry => [entry.label.toLowerCase(), entry.count])
	);
	const distribution = {} as EmotionDistribution;
	for (const emotion of EMOTIONS) {
		distribution[emotion] = counts.get(emotion) ?? 0;
	}
	return distribution;
}

// CONTRACT: each row carries its `handle` and its `voice_share` — in percent,
// of every influencer-attributable mention: the block's own total is only the
// listed rows' and would inflate every share.
export function sourceTopInfluencers(
	blocks: ReportBlockData[]
): TopInfluencerByVoiceShare[] {
	return (drawnBlock(blocks)?.rows ?? []).map(row => {
		const mentions = numberOf(row['mentions']);
		const positive = numberOf(row['positive']);
		const negative = numberOf(row['negative']);
		const base = Math.max(mentions, 1);
		const name = textOf(row['author']);
		return {
			name,
			handle: textOf(row['handle']) || name,
			avatarUrl: null,
			platform: platformId(textOf(row['platform'])),
			platformIconUrl: null,
			mentions,
			mentionsNegative: percentOf(negative, base),
			mentionsNeutral: percentOf(
				Math.max(mentions - positive - negative, 0),
				base
			),
			mentionsPositive: percentOf(positive, base),
			voiceShare: numberOf(row['voice_share'])
		};
	});
}

// CONTRACT: each row carries the site's monthly `visits`, and may say how far
// they could be read (`visits_status`). Without it a site with no visits is
// one whose visits are unknown, drawn as a dash and never as a zero.
export function sourceTopSites(blocks: ReportBlockData[]): TopSiteByVisits[] {
	return (drawnBlock(blocks)?.rows ?? []).map(row => {
		const mentions = numberOf(row['mentions']);
		const positive = numberOf(row['positive']);
		const negative = numberOf(row['negative']);
		const base = Math.max(mentions, 1);
		const visits = numberOf(row['visits']);
		const status = textOf(row['visits_status']);
		return {
			name: textOf(row['site']),
			avatarUrl: null,
			mentions,
			mentionsNegative: percentOf(negative, base),
			mentionsNeutral: percentOf(
				Math.max(mentions - positive - negative, 0),
				base
			),
			mentionsPositive: percentOf(positive, base),
			visits,
			visitsStatus:
				status === 'ok' ||
				status === 'pending' ||
				status === 'unavailable'
					? status
					: visits > 0
					? 'ok'
					: 'unavailable'
		};
	});
}

export function sourceAuthorsByPlatform(
	blocks: ReportBlockData[]
): MediaTypeInfluencer[] {
	return countsOf(blocks).map(entry => ({
		platform: platformId(entry.label),
		count: entry.count
	}));
}

// CONTRACT: "12 AM" … "11 PM", the analytics endpoint's hour, which the slide
// parses back.
function hourLabel(hour: number): string {
	const twelve = hour % 12 === 0 ? 12 : hour % 12;
	return `${twelve} ${hour < 12 ? 'AM' : 'PM'}`;
}

// CONTRACT: the resolver sends only the hours somebody engaged in, and the
// analytics endpoint fills the rest of the week with zeros.
export function sourceBestTimeCells(
	blocks: ReportBlockData[]
): BestTimeToPostCell[] {
	const cells = new Map<string, number>();
	for (const row of drawnBlock(blocks)?.rows ?? []) {
		cells.set(
			`${textOf(row['day'])}|${numberOf(row['hour'])}`,
			numberOf(row['value'])
		);
	}
	if (!cells.size) {
		return [];
	}
	return WEEKDAYS.flatMap(day =>
		Array.from({ length: 24 }, (_, hour) => ({
			day,
			hour: hourLabel(hour),
			value: cells.get(`${day}|${hour}`) ?? 0
		}))
	);
}

export function sourceTopCountries(
	blocks: ReportBlockData[]
): TopCountryItem[] {
	const rows = drawnBlock(blocks)?.rows ?? [];
	return countsOf(blocks).map((entry, index) => ({
		code: textOf(rows[index]?.['code']) || entry.label,
		name: entry.label,
		mentions: entry.count
	}));
}

export function sourceTrendingHashtags(
	blocks: ReportBlockData[]
): TrendingHashtag[] {
	return countsOf(blocks).map(entry => ({
		hashtag: entry.label,
		count: entry.count
	}));
}

export function sourceTrendingConversations(
	blocks: ReportBlockData[]
): TrendingConversation[] {
	return countsOf(blocks).map(entry => ({
		context: entry.label,
		count: entry.count
	}));
}

export function sourceMentionsByPlatform(
	blocks: ReportBlockData[]
): MentionsByMediaTypePlatform[] {
	const counts = countsOf(blocks);
	const total = counts.reduce((sum, entry) => sum + entry.count, 0);
	return counts.map(entry => ({
		platform: platformId(entry.label),
		count: entry.count,
		percentage: percentOf(entry.count, total)
	}));
}

export function sourceLanguages(blocks: ReportBlockData[]): LanguageTop[] {
	const counts = countsOf(blocks);
	const total = counts.reduce((sum, entry) => sum + entry.count, 0);
	return counts.map(entry => ({
		language: entry.label,
		count: entry.count,
		percentage: percentOf(entry.count, total)
	}));
}

// TRAP: null unless the answer holds all ten — a tile it lacks would print a
// zero nobody counted; the section is then drawn from what it does hold.
// CONTRACT: the tiles come as rows `{key, value, changes, share}` — `key` the
// analytics-info field (`total_mentions`, `likes`, …), `changes` the weekly
// change in percent or null, `share` on the two sentiment tiles the share of
// all mentions as a 0-1 fraction.
export function sourceHeadlineTiles(
	blocks: ReportBlockData[]
): AnalyticsInfoResponse | null {
	const tiles = new Map<string, Record<string, unknown>>();
	for (const block of blocks) {
		for (const row of block.empty ? [] : block.rows ?? []) {
			const key = row['key'];
			if (typeof key === 'string') {
				tiles.set(key, row);
			}
		}
	}
	if (!HEADLINE_TILE_KEYS.every(key => tiles.has(key))) {
		return null;
	}
	const value = (key: string): number => numberOf(tiles.get(key)?.['value']);
	const changes = (key: string): number | null =>
		numberOrNull(tiles.get(key)?.['changes']);
	const share = (key: string): number | null =>
		numberOrNull(tiles.get(key)?.['share']);
	return {
		totalMentions: value('total_mentions'),
		totalMentionsChanges: changes('total_mentions'),
		// CONTRACT: not tiles the Executive Summary draws — read when the
		// answer has them, zero otherwise.
		authorityScore: value('authority_score'),
		authorityScoreChanges: changes('authority_score'),
		authorityScoreBadge: null,
		totalReach: value('total_reach'),
		totalReachChanges: changes('total_reach'),
		totalEngagements: value('total_engagements'),
		totalEngagementsChanges: changes('total_engagements'),
		engagementRate: value('engagement_rate'),
		engagementRateChanges: changes('engagement_rate'),
		engagementRateBadge: null,
		reputation: value('reputation'),
		reputationChanges: changes('reputation'),
		reputationBadge: null,
		likes: value('likes'),
		likesChanges: changes('likes'),
		socialMediaReach: value('social_media_reach'),
		socialMediaReachChanges: changes('social_media_reach'),
		webReach: value('web_reach'),
		webReachChanges: changes('web_reach'),
		webSites: value('web_sites'),
		webSitesChanges: changes('web_sites'),
		socialMentions: value('social_mentions'),
		socialMentionsChanges: changes('social_mentions'),
		webMentions: value('web_mentions'),
		webMentionsChanges: changes('web_mentions'),
		positiveMentions: value('positive_mentions'),
		positiveShare: share('positive_mentions'),
		positiveMentionsChanges: changes('positive_mentions'),
		negativeMentions: value('negative_mentions'),
		negativeShare: share('negative_mentions'),
		negativeMentionsChanges: changes('negative_mentions')
	};
}
